import { Fragment, useEffect, useRef, useState } from 'react'

import PropTypes from 'prop-types'

import Icon from 'components/Core/Icon/Icon'
import Table from 'components/Core/Table/Table'

import ItemTableBodyRow from './ItemTableBodyRow'

const ItemTableExpandableBodyRow = ({
  item,
  columns,
  columnOrder,
  className: _className,
  onRowClick: _onRowClick,
  rowClassnames,
  rowsExpanded,
  aggregationKeys,
  onExpandAnyRow,
  testId,
  ...rest
}) => {
  const [open, setOpen] = useState(new Set())

  const { expandableRow = '', expandedRow = '' } = rowClassnames || {}

  const openKeys = useRef(new Set())

  useEffect(() => {
    rowsExpanded ? setOpen(openKeys.current) : setOpen(new Set())
  }, [rowsExpanded])

  const generateRow = (item, open, setOpen, parentPath = []) => {
    const aggregationKey = aggregationKeys.find((key) => !!item[key])
    const groupPath = aggregationKey
      ? [...parentPath, item[aggregationKey]]
      : parentPath
    openKeys.current.add(item[aggregationKey])
    const { subItems: expandableItems = [] } = item

    const handleExpansion = (isOpen) => {
      const set = new Set(open)
      isOpen ? set.delete(item[aggregationKey]) : set.add(item[aggregationKey])
      set.size === 0 ? onExpandAnyRow(false) : onExpandAnyRow(true)
      setOpen(set)
    }

    return (
      <Fragment key={aggregationKey ? `group-${groupPath.join('-')}` : item.id}>
        {expandableItems?.length > 0 &&
          renderExpandableRow(
            item,
            handleExpansion,
            open.has(item[aggregationKey]),
            groupPath.join('-'),
          )}
        {expandableItems?.length > 0 && open.has(item[aggregationKey]) && (
          <>
            {expandableItems.map((child) => {
              return generateRow(child, open, setOpen, groupPath)
            })}
          </>
        )}
        {!expandableItems?.length && <>{renderExpandedRow(item)}</>}
      </Fragment>
    )
  }

  const renderExpansionIcon = (isRowExpanded) => {
    const iconName = isRowExpanded ? 'dropup' : 'chevron'
    return <Icon name={iconName} />
  }

  const renderExpandedRow = (item) => {
    return (
      <ItemTableBodyRow
        className={expandedRow}
        isExpandedRow={true}
        selectable={false}
        columnOrder={columnOrder}
        columns={columns}
        item={item}
        testId={testId}
      />
    )
  }

  const renderExpandableRow = (item, handleExpansion, isOpen, groupKey) => {
    const rowTestId = testId ? `${testId}-group-${groupKey}` : undefined
    return (
      <Table.Row
        className={expandableRow}
        onClick={() => handleExpansion(isOpen)}
        testId={rowTestId}
      >
        <Table.Cell testId={rowTestId ? `${rowTestId}-expand` : undefined}>
          {renderExpansionIcon(isOpen)}
        </Table.Cell>
        {columnOrder.map((id) => (
          <Table.Cell
            key={id}
            testId={rowTestId ? `${rowTestId}-cell-${id}` : undefined}
            width={columns[id].width}
            className={
              columns[id].cellClassName && columns[id].cellClassName(item)
            }
            {...columns[id].align}
            {...rest}
          >
            {columns[id].cellContent(item)}
          </Table.Cell>
        ))}
      </Table.Row>
    )
  }

  return <>{generateRow(item, open, setOpen)}</>
}

ItemTableExpandableBodyRow.propTypes = {
  /* item to be used to calculate cell contents */
  item: PropTypes.object.isRequired,
  /*
  The prop columns takes an object containing columnId as key.
  */
  columns: PropTypes.object.isRequired,
  /* array of Column Id in the order that they should be displayed */
  columnOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  onRowClick: PropTypes.func,
  rowsExpanded: PropTypes.bool,
  /* array of properties of object to use for building the different levels */
  aggregationKeys: PropTypes.arrayOf(PropTypes.string),
  onExpandAnyRow: PropTypes.func,
  /* Table-level id. A group row gets `{testId}-group-{path}`, where path joins
     the aggregation values from the top level down with `-` (e.g.
     `{testId}-group-{style}-{color}`), its toggle cell `{...}-expand` and cells
     `{...}-cell-{columnId}`; leaf rows use the ItemTableBodyRow ids
     (`{testId}-row-{item.id}`). */
  testId: PropTypes.string,
}

ItemTableExpandableBodyRow.defaultProps = {}

export default ItemTableExpandableBodyRow
